import React, { useEffect, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { cooperplaceApi } from 'services/api';
import { selectProps } from './OptionField';
import { normalizePlate, validPlate, validateOptions } from './contract';

const separator = /[\s,;]+/;
/** Placas válidas de um texto digitado ou colado, somadas à seleção atual sem repetir. */
export function mergePlates(value, text) {
  const tokens = text.split(separator).filter(Boolean).map(normalizePlate);
  return {
    plates: [...new Set([...value, ...tokens.filter(validPlate)])],
    invalid: tokens.filter((token) => !validPlate(token)),
  };
}

/**
 * Placas digitadas, coladas ou escolhidas nas sugestões (5713). As sugestões vêm de todo o
 * cadastro de veículos e implementos (catálogo plates, 5717), a partir de 2 caracteres.
 */
export default function PlateField({ id, label, value, onChange, onPending }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const [state, setState] = useState({ options: [], loading: false, failed: false });
  const generation = useRef(0);
  const search = normalizePlate(input);
  useEffect(() => {
    const current = ++generation.current;
    if (search.length < 2) {
      setState({ options: [], loading: false, failed: false });
      return undefined;
    }
    const abort = new AbortController();
    setState((s) => ({ ...s, loading: true, failed: false }));
    const timer = setTimeout(async () => {
      try {
        const { data } = await cooperplaceApi.get('control-tower/load-filter-options', {
          params: { field: 'plates', scope: 'travels', search, page: 1, perPage: 25 },
          signal: abort.signal,
        });
        const options = validateOptions(data, 'plates', 1, 25).data;
        if (generation.current === current) setState({ options, loading: false, failed: false });
      } catch {
        if (!abort.signal.aborted && generation.current === current)
          setState({ options: [], loading: false, failed: true });
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [search]);
  const type = (text) => {
    setInput(text);
    onPending(text);
  };
  const add = (text) => {
    if (!text.split(separator).some(Boolean)) return;
    const { plates, invalid } = mergePlates(value, text);
    if (plates.length > 100) {
      setError('Informe até 100 placas.');
      return;
    }
    onChange(plates);
    type('');
    setError(
      invalid.length
        ? `Ignorado: ${invalid.join(', ')}. Informe placas completas, com 7 letras ou números.`
        : null,
    );
  };
  const typed = validPlate(search) && !value.includes(search);
  return (
    <div className="tc-f-field" data-filter-field={id}>
      <label htmlFor={id}>{label}</label>
      <CreatableSelect
        {...selectProps}
        inputId={id}
        instanceId={id}
        aria-label={label}
        aria-describedby={error ? `${id}-error` : undefined}
        placeholder="Informe ou busque a placa"
        components={{ DropdownIndicator: null, IndicatorSeparator: null }}
        menuPortalTarget={document.body}
        menuIsOpen={search.length >= 2}
        inputValue={input}
        options={state.options}
        isLoading={state.loading}
        filterOption={null}
        tabSelectsValue={false}
        isValidNewOption={() => typed && !state.options.some((o) => o.value === search)}
        formatCreateLabel={() => `Usar a placa ${search}`}
        noOptionsMessage={() =>
          state.failed ? 'Busca indisponível' : 'Nenhuma placa encontrada no cadastro'
        }
        value={value.map((plate) => ({ value: plate, label: plate }))}
        onChange={(items) => {
          onChange((items || []).map((o) => o.value));
          type('');
          setError(null);
        }}
        onCreateOption={add}
        onInputChange={(text, { action }) => {
          if (action !== 'input-change') return;
          setError(null);
          if (separator.test(text)) add(text);
          else type(text.slice(0, 100));
        }}
        onBlur={() => input && add(input)}
        isOptionDisabled={(option) => value.length >= 100 && !value.includes(option.value)}
      />
      {error && (
        <span id={`${id}-error`} role="alert" className="tc-f-search-error">
          {error}
        </span>
      )}
    </div>
  );
}
